import axios from 'axios';
import API_URL from '../api/config';

const api = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Get all active plans with features (public, no auth needed)
 */
export const getPlans = () =>
  api.get('/billing/plans').then(res => res.data);

/**
 * Get Stripe publishable key (public)
 */
export const getBillingConfig = () =>
  api.get('/billing/config').then(res => res.data);

/**
 * Create a Stripe Checkout session and return the URL
 * affiliateCode and discountCode are optional
 */
export const createCheckout = (planId, interval = 'monthly', affiliateCode, discountCode) =>
  api.post('/billing/checkout', { planId, interval, affiliateCode, discountCode }).then(res => res.data);

/**
 * Create a Stripe Customer Portal session and return the URL
 */
export const createPortalSession = () =>
  api.post('/billing/portal').then(res => res.data);

/**
 * Cancel subscription (at period end by default)
 */
export const cancelSubscription = (immediate = false) =>
  api.post('/billing/cancel', { immediate }).then(res => res.data);

/**
 * Resubscribe (remove cancel_at_period_end)
 */
export const resubscribe = () =>
  api.post('/billing/resubscribe').then(res => res.data);

/**
 * Change plan for an existing subscriber (upgrade or downgrade).
 * Codes apply to upgrades only. Discounts ignored on downgrades.
 * Returns { action: 'upgraded'|'scheduled'|'no_change', message, ... } or { url } for checkout fallback.
 */
export const changePlan = (planId, interval = 'monthly', affiliateCode, discountCode) =>
  api.post('/billing/change-plan', { planId, interval, affiliateCode, discountCode }).then(res => res.data);

/**
 * Cancel a pending plan downgrade (release Stripe subscription schedule)
 */
export const cancelPendingChange = () =>
  api.post('/billing/cancel-pending-change').then(res => res.data);

/**
 * Verify a Stripe Checkout session and activate the subscription.
 * Fallback for when webhooks are delayed or misconfigured.
 */
export const verifyCheckoutSession = (sessionId) =>
  api.get(`/billing/verify-session?session_id=${encodeURIComponent(sessionId)}`).then(res => res.data);

/**
 * Get current subscription details + usage meters
 */
export const getSubscription = () =>
  api.get('/billing/subscription').then(res => res.data);
